import { api } from "@/api/client";

// ── Типы (зеркалят seller_helper/product_dev/schemas.py) ────────────────────

export interface OneCRefView {
  one_c_kind: string | null;
  one_c_ref_key: string | null;
  one_c_char_key: string | null;
  name_source: string;
  sync_status: string;
  bound_at: string | null;
  is_bound: boolean;
}

export interface ProjectView {
  id: string;
  name: string;
  stage: string;
  category: string | null;
  description: string | null;
  target_date: string | null;
  owner_user_id: number | null;
  owner_username: string | null;
  approved_by_user_id: number | null;
  approved_at: string | null;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
  output_count: number | null;
  attachment_count: number | null;
  open_checklist_count: number | null;
}

export interface MaterialView {
  id: string;
  display_name: string;
  material_type: string;
  unit: string;
  price: number | null;
  currency: string;
  price_source: string;
  price_detail: string | null;
  note: string | null;
  onec: OneCRefView;
  created_at: string;
  updated_at: string;
}

export interface RefreshPricesResult {
  updated: number;
  no_receipt: number;
  skipped_offer: number;
  not_bound: number;
}

export interface SupplierView {
  id: string;
  display_name: string;
  contact: string | null;
  note: string | null;
  onec: OneCRefView;
}

export interface OfferView {
  id: string;
  material_id: string;
  material_name: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  project_id: string | null;
  price: number;
  currency: string;
  unit: string;
  moq: number | null;
  lead_time_days: number | null;
  conditions: string | null;
  quoted_at: string | null;
  valid_until: string | null;
  is_selected: boolean;
  created_at: string;
}

export interface BomItemView {
  id: number;
  output_id: string;
  line_no: number;
  material_id: string | null;
  child_output_id: string | null;
  ref_name: string;
  ref_kind: "material" | "output";
  material_type: string | null;
  qty: number;
  unit: string;
  loss_coeff: number;
  comment: string | null;
}

export interface BomOutputView {
  id: string;
  project_id: string;
  peredel: string;
  channel: string | null;
  title: string | null;
  display_name: string;
  is_approved: boolean;
  onec: OneCRefView;
  items: BomItemView[];
}

export interface BomTreeNode {
  output_id: string;
  peredel: string;
  channel: string | null;
  display_name: string;
  qty_per_parent: number;
  lines: BomTreeLine[];
}
export interface BomTreeLine {
  item_id: number;
  ref_kind: "material" | "output";
  ref_name: string;
  material_type: string | null;
  qty: number;
  unit: string;
  loss_coeff: number;
  comment: string | null;
  child: BomTreeNode | null;
}
export interface BomTreeView {
  project_id: string;
  roots: BomTreeNode[];
  warnings: string[];
}

export interface RollupLine {
  material_id: string | null;
  material_name: string;
  material_type: string | null;
  qty_total: number;
  unit: string;
  unit_price: number | null;
  currency: string | null;
  price_source: string | null;
  line_cost: number | null;
  has_price: boolean;
}
export interface RollupRoot {
  output_id: string;
  display_name: string;
  peredel: string;
  channel: string | null;
  currency: string;
  total_cost: number | null;
  lines: RollupLine[];
  warnings: string[];
}
export interface RollupView {
  project_id: string;
  roots: RollupRoot[];
  warnings: string[];
}

export interface AttachmentView {
  id: string;
  project_id: string;
  material_id: string | null;
  kind: string;
  title: string | null;
  caption: string | null;
  url: string | null;
  file_ref: string | null;
  version: number;
  is_approved: boolean;
  is_final: boolean;
  created_at: string;
}

export interface NoteView {
  id: number;
  project_id: string;
  author_user_id: number | null;
  author_username: string | null;
  body: string;
  created_at: string;
}

export interface ChecklistItemView {
  id: number;
  project_id: string;
  label: string;
  status: string;
  responsible: string | null;
  due_date: string | null;
  sort_order: number;
  note: string | null;
}

export interface CostSnapshotLineView {
  material_id: string | null;
  material_name: string | null;
  qty_total: number;
  unit: string;
  unit_price: number | null;
  price_source: string | null;
  price_detail: string | null;
  line_cost: number | null;
}
export interface CostSnapshotView {
  id: string;
  project_id: string;
  output_id: string | null;
  output_title: string | null;
  taken_at: string;
  currency: string;
  total_cost: number | null;
  missing_price_count: number;
  note: string | null;
  lines: CostSnapshotLineView[];
}

export interface TechCardView {
  project: ProjectView;
  tree: BomTreeView;
  rollup: RollupView;
  final_photos: AttachmentView[];
  approved_designs: AttachmentView[];
  checklist: ChecklistItemView[];
}

export interface PdevMeta {
  stages: Record<string, string>;
  peredels: Record<string, string>;
  channels: Record<string, string>;
  material_types: Record<string, string>;
  checklist_statuses: Record<string, string>;
  attachment_kinds: Record<string, string>;
}

export interface OneCSearchResult {
  ref_key: string;
  name: string | null;
  article?: string | null;
  inn?: string | null;
  image_file_ref?: string | null;
}

const B = "/product-dev";

// ── Meta ────────────────────────────────────────────────────────────────────
export const getMeta = async (): Promise<PdevMeta> => (await api.get<PdevMeta>(`${B}/meta`)).data;

// ── Проекты ──────────────────────────────────────────────────────────────────
export const listProjects = async (stage?: string): Promise<ProjectView[]> =>
  (await api.get<ProjectView[]>(`${B}/projects`, { params: stage ? { stage } : undefined })).data;
export const getProject = async (id: string): Promise<ProjectView> =>
  (await api.get<ProjectView>(`${B}/projects/${id}`)).data;
export const createProject = async (body: {
  name: string;
  category?: string | null;
  description?: string | null;
  target_date?: string | null;
}): Promise<ProjectView> => (await api.post<ProjectView>(`${B}/projects`, body)).data;
export const updateProject = async (id: string, body: Partial<ProjectView>): Promise<ProjectView> =>
  (await api.put<ProjectView>(`${B}/projects/${id}`, body)).data;
export const deleteProject = async (id: string): Promise<void> => {
  await api.delete(`${B}/projects/${id}`);
};
export const approveProject = async (id: string, note?: string): Promise<CostSnapshotView[]> =>
  (await api.post<CostSnapshotView[]>(`${B}/projects/${id}/approve`, { note })).data;
export const reopenProject = async (id: string): Promise<ProjectView> =>
  (await api.post<ProjectView>(`${B}/projects/${id}/reopen`)).data;
export const listCostSnapshots = async (id: string): Promise<CostSnapshotView[]> =>
  (await api.get<CostSnapshotView[]>(`${B}/projects/${id}/cost-snapshots`)).data;

// ── BOM ──────────────────────────────────────────────────────────────────────
export const getBomTree = async (id: string): Promise<BomTreeView> =>
  (await api.get<BomTreeView>(`${B}/projects/${id}/bom-tree`)).data;
export const getRollup = async (id: string): Promise<RollupView> =>
  (await api.get<RollupView>(`${B}/projects/${id}/rollup`)).data;
export const listOutputs = async (id: string): Promise<BomOutputView[]> =>
  (await api.get<BomOutputView[]>(`${B}/projects/${id}/outputs`)).data;
export const createOutput = async (
  id: string,
  body: { peredel: string; channel?: string | null; title?: string | null },
): Promise<BomOutputView> => (await api.post<BomOutputView>(`${B}/projects/${id}/outputs`, body)).data;
export const deleteOutput = async (outputId: string): Promise<void> => {
  await api.delete(`${B}/outputs/${outputId}`);
};
export const createBomItem = async (body: {
  output_id: string;
  material_id?: string | null;
  child_output_id?: string | null;
  qty: number;
  loss_coeff?: number;
  comment?: string | null;
}): Promise<BomItemView> => (await api.post<BomItemView>(`${B}/bom-items`, body)).data;
export const updateBomItem = async (
  itemId: number,
  body: { qty?: number; loss_coeff?: number; comment?: string | null },
): Promise<BomItemView> => (await api.put<BomItemView>(`${B}/bom-items/${itemId}`, body)).data;
export const deleteBomItem = async (itemId: number): Promise<void> => {
  await api.delete(`${B}/bom-items/${itemId}`);
};

// ── Материалы (каталог) ──────────────────────────────────────────────────────
export const listMaterials = async (params?: { material_type?: string; q?: string }): Promise<MaterialView[]> =>
  (await api.get<MaterialView[]>(`${B}/materials`, { params })).data;
export const createMaterial = async (body: {
  local_name: string;
  material_type: string;
  unit?: string;
  price?: number | null;
  note?: string | null;
}): Promise<MaterialView> => (await api.post<MaterialView>(`${B}/materials`, body)).data;
export const updateMaterial = async (
  id: string,
  body: { local_name?: string; material_type?: string; unit?: string; price?: number | null; note?: string | null },
): Promise<MaterialView> => (await api.put<MaterialView>(`${B}/materials/${id}`, body)).data;
export const deleteMaterial = async (id: string): Promise<void> => {
  await api.delete(`${B}/materials/${id}`);
};
export const bindMaterial1C = async (
  id: string,
  body: { one_c_kind: string; one_c_ref_key: string; one_c_char_key?: string | null; display_name?: string | null },
): Promise<MaterialView> => (await api.post<MaterialView>(`${B}/materials/${id}/bind-1c`, body)).data;
export const materialOffers = async (id: string): Promise<OfferView[]> =>
  (await api.get<OfferView[]>(`${B}/materials/${id}/offers`)).data;
/** Цена материала = последняя закупка из 1С (для привязанной номенклатуры). */
export const refreshMaterialPrice1C = async (id: string): Promise<MaterialView> =>
  (await api.post<MaterialView>(`${B}/materials/${id}/refresh-price-1c`)).data;
/** Обновить цены всех материалов состава из последних закупок 1С. */
export const refreshProjectPrices1C = async (projectId: string): Promise<RefreshPricesResult> =>
  (await api.post<RefreshPricesResult>(`${B}/projects/${projectId}/refresh-prices-1c`)).data;

// Лайв-поиск в 1С + создание материала из 1С.
export const searchNomenclature1C = async (q: string, limit = 20): Promise<OneCSearchResult[]> =>
  (await api.get<OneCSearchResult[]>(`${B}/materials/search-1c`, { params: { q, limit } })).data;
export const searchSupplier1C = async (q: string, limit = 20): Promise<OneCSearchResult[]> =>
  (await api.get<OneCSearchResult[]>(`${B}/suppliers/search-1c`, { params: { q, limit } })).data;
export const createMaterialFrom1C = async (body: {
  one_c_ref_key: string;
  one_c_char_key?: string | null;
  name: string;
  material_type: string;
  unit?: string;
}): Promise<MaterialView> => (await api.post<MaterialView>(`${B}/materials/from-1c`, body)).data;
/** Тянет картинку номенклатуры 1С под аутентификацией → object-URL (для миниатюр). */
export const fetch1CImageBlobUrl = async (fileRef: string): Promise<string> => {
  const { data } = await api.get(`${B}/materials/1c-image`, {
    params: { file_ref: fileRef },
    responseType: "blob",
  });
  return URL.createObjectURL(data as Blob);
};

// ── Поставщики / офферы ──────────────────────────────────────────────────────
export const listSuppliers = async (): Promise<SupplierView[]> =>
  (await api.get<SupplierView[]>(`${B}/suppliers`)).data;
export const createSupplier = async (body: { local_name: string; contact?: string | null }): Promise<SupplierView> =>
  (await api.post<SupplierView>(`${B}/suppliers`, body)).data;
export const updateSupplier = async (
  id: string,
  body: { local_name?: string; contact?: string | null; note?: string | null },
): Promise<SupplierView> => (await api.put<SupplierView>(`${B}/suppliers/${id}`, body)).data;
export const deleteSupplier = async (id: string): Promise<void> => {
  await api.delete(`${B}/suppliers/${id}`);
};
export const bindSupplier1C = async (
  id: string,
  body: { one_c_kind: string; one_c_ref_key: string; display_name?: string | null },
): Promise<SupplierView> => (await api.post<SupplierView>(`${B}/suppliers/${id}/bind-1c`, body)).data;
export const listProjectOffers = async (projectId: string): Promise<OfferView[]> =>
  (await api.get<OfferView[]>(`${B}/projects/${projectId}/offers`)).data;
export const updateOffer = async (
  offerId: string,
  body: { price?: number; conditions?: string | null; supplier_id?: string | null },
): Promise<OfferView> => (await api.put<OfferView>(`${B}/offers/${offerId}`, body)).data;
export const deleteOffer = async (offerId: string): Promise<void> => {
  await api.delete(`${B}/offers/${offerId}`);
};
export const createOffer = async (body: {
  material_id: string;
  supplier_id?: string | null;
  project_id?: string | null;
  price: number;
  conditions?: string | null;
  moq?: number | null;
  lead_time_days?: number | null;
  quoted_at?: string | null;
}): Promise<OfferView> => (await api.post<OfferView>(`${B}/offers`, body)).data;
export const selectOffer = async (offerId: string): Promise<MaterialView> =>
  (await api.post<MaterialView>(`${B}/offers/${offerId}/select`)).data;

// ── Вложения ─────────────────────────────────────────────────────────────────
export const listAttachments = async (id: string): Promise<AttachmentView[]> =>
  (await api.get<AttachmentView[]>(`${B}/projects/${id}/attachments`)).data;
export const createAttachment = async (
  id: string,
  body: {
    kind: string;
    title?: string | null;
    caption?: string | null;
    url?: string | null;
    file_ref?: string | null;
    is_final?: boolean;
    is_approved?: boolean;
  },
): Promise<AttachmentView> => (await api.post<AttachmentView>(`${B}/projects/${id}/attachments`, body)).data;
export const updateAttachment = async (
  attId: string,
  body: { is_final?: boolean; is_approved?: boolean; title?: string | null },
): Promise<AttachmentView> => (await api.put<AttachmentView>(`${B}/attachments/${attId}`, body)).data;
export const deleteAttachment = async (attId: string): Promise<void> => {
  await api.delete(`${B}/attachments/${attId}`);
};
export const uploadAttachment = async (
  projectId: string,
  file: File,
  opts: { kind: string; title?: string },
): Promise<AttachmentView> => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", opts.kind);
  if (opts.title) fd.append("title", opts.title);
  const { data } = await api.post<AttachmentView>(
    `${B}/projects/${projectId}/attachments/upload`,
    fd,
  );
  return data;
};
/** Тянет файл вложения под аутентификацией и отдаёт object-URL (для <img>/<a>). */
export const fetchAttachmentBlobUrl = async (attId: string): Promise<string> => {
  const { data } = await api.get(`${B}/attachments/${attId}/file`, { responseType: "blob" });
  return URL.createObjectURL(data as Blob);
};

// ── Заметки ──────────────────────────────────────────────────────────────────
export const listNotes = async (id: string): Promise<NoteView[]> =>
  (await api.get<NoteView[]>(`${B}/projects/${id}/notes`)).data;
export const createNote = async (id: string, body: string): Promise<NoteView> =>
  (await api.post<NoteView>(`${B}/projects/${id}/notes`, { body })).data;

// ── Чек-лист ─────────────────────────────────────────────────────────────────
export const listChecklist = async (id: string): Promise<ChecklistItemView[]> =>
  (await api.get<ChecklistItemView[]>(`${B}/projects/${id}/checklist`)).data;
export const updateChecklistItem = async (
  itemId: number,
  body: { status?: string; responsible?: string | null; note?: string | null; label?: string },
): Promise<ChecklistItemView> => (await api.put<ChecklistItemView>(`${B}/checklist/${itemId}`, body)).data;
export const createChecklistItem = async (
  id: string,
  body: { label: string; responsible?: string | null },
): Promise<ChecklistItemView> => (await api.post<ChecklistItemView>(`${B}/projects/${id}/checklist`, body)).data;

// ── Техкарта ─────────────────────────────────────────────────────────────────
export const getTechCard = async (id: string): Promise<TechCardView> =>
  (await api.get<TechCardView>(`${B}/projects/${id}/tech-card`)).data;
